// frontend/src/pages/ReporteGevesVista.jsx
// Vista pública del reporte — no requiere autenticación
// Solo muestra Mi Marca + Competencia (sin Referencia)
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import contentService from '../services/contentService';
import './ReporteGeves.css';
import './ReporteGevesVista.css';

import Loading from '../components/Loading';
/* ── helpers ── */
const fmtN = (n) => {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return Number(n).toLocaleString('es-CL');
};
const fmtAxis = (n, range) => {
  if (n == null) return '—';
  if (n >= 1_000_000) {
    const decimals = range < 100_000 ? 3 : range < 1_000_000 ? 2 : 1;
    return `${(n / 1_000_000).toFixed(decimals)}M`;
  }
  if (n >= 1_000) {
    const decimals = range < 1_000 ? 3 : range < 5_000 ? 2 : 1;
    return `${(n / 1_000).toFixed(decimals)}K`;
  }
  return Number(n).toLocaleString('es-CL');
};
const fmtPct = (n) => (n == null ? '—' : `${Number(n).toFixed(2)}%`);
const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
};
const isoToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};
const iso30DaysAgo = () => {
  const d = new Date(); d.setDate(d.getDate() - 30);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

const TYPE_META = {
  VIDEO:          { label: 'Reel',     color: '#e1306c', bg: '#fce4ec' },
  IMAGE:          { label: 'Foto',     color: '#1565c0', bg: '#e3f2fd' },
  CAROUSEL_ALBUM: { label: 'Carrusel', color: '#4527a0', bg: '#ede7f6' },
};

/* ── Badge tipo ── */
function TypeBadge({ mediaType }) {
  const m = TYPE_META[mediaType] || { label: mediaType || '—', color: '#475569', bg: '#f1f5f9' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 20,
      fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
      background: m.bg, color: m.color, whiteSpace: 'nowrap',
    }}>{m.label}</span>
  );
}

/* ── Avatar ── */
function Avatar({ profile, size = 32 }) {
  const [err, setErr] = useState(false);
  const initials = (profile.username || '').slice(0, 2).toUpperCase();
  if (profile.profile_pic_url && !err) {
    return (
      <img src={profile.profile_pic_url} alt="" onError={() => setErr(true)}
        style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', background: '#e0e7ff',
      color: '#4338ca', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontSize: size * 0.35, flexShrink: 0,
    }}>{initials}</div>
  );
}

/* ── KPI Card ── */
function KpiCard({ label, value, sub }) {
  return (
    <div className="rg2-kpi">
      <div className="rg2-kpi-lbl">{label}</div>
      <div className="rg2-kpi-val">{value}</div>
      {sub && <div className="rg2-kpi-sub">{sub}</div>}
    </div>
  );
}

/* ── Tooltip flotante para charts ── */
function ChartTooltip({ tip }) {
  if (!tip) return null;
  const style = {
    position: 'absolute',
    left: tip.x + 14,
    top: tip.y - 44,
    background: '#002421',
    color: '#fff',
    padding: '6px 12px',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    pointerEvents: 'none',
    zIndex: 200,
    whiteSpace: 'nowrap',
    boxShadow: '0 4px 12px rgba(0,36,33,0.25)',
  };
  return (
    <div style={style}>
      {tip.label && <div style={{ color: '#71d234', fontSize: 10, fontWeight: 700, marginBottom: 2 }}>{tip.label}</div>}
      <div>{tip.value}</div>
    </div>
  );
}

/* ── Mini bar chart SVG ── */
function BarChart({ posts, metric, color, label }) {
  const [tip, setTip] = useState(null);
  const wrapRef = useRef(null);

  const data = posts
    .filter(p => p[metric] != null)
    .slice()
    .sort((a, b) => new Date(a.posted_at) - new Date(b.posted_at))
    .slice(-30);

  if (!data.length) return <div className="rg2-chart-empty">Sin datos para {label}</div>;

  const W = 600, H = 140, PAD = { t: 16, r: 8, b: 28, l: 44 };
  const innerW = W - PAD.l - PAD.r;
  const innerH = H - PAD.t - PAD.b;

  const vals = data.map(p => p[metric] || 0);
  const maxVal = Math.max(...vals, 1);
  const barW = Math.max(4, innerW / data.length - 2);
  const step = innerW / data.length;

  const yTicks = 4;
  const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
  const avgY = PAD.t + innerH - (avg / maxVal) * innerH;

  const showTip = (e, p) => {
    const rect = wrapRef.current.getBoundingClientRect();
    setTip({ x: e.clientX - rect.left, y: e.clientY - rect.top, label: fmtDate(p.posted_at), value: fmtN(p[metric]) });
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <ChartTooltip tip={tip} />
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }} onMouseLeave={() => setTip(null)}>
        {Array.from({ length: yTicks + 1 }, (_, i) => {
          const y = PAD.t + (innerH / yTicks) * i;
          const val = Math.round(maxVal - (maxVal / yTicks) * i);
          return (
            <g key={i}>
              <line x1={PAD.l} x2={W - PAD.r} y1={y} y2={y} stroke="#f1f5f9" strokeWidth={1} />
              <text x={PAD.l - 4} y={y + 4} textAnchor="end" fontSize={7} fill="#94a3b8">{fmtN(val)}</text>
            </g>
          );
        })}
        <line x1={PAD.l} x2={W - PAD.r} y1={avgY} y2={avgY}
          stroke={color} strokeWidth={0.8} strokeDasharray="4 3" opacity={0.5} />
        {data.map((p, i) => {
          const val = p[metric] || 0;
          const barH = (val / maxVal) * innerH;
          const x = PAD.l + i * step + (step - barW) / 2;
          const y = PAD.t + innerH - barH;
          return (
            <g key={p.id}
              onMouseEnter={e => showTip(e, p)}
              onMouseMove={e => showTip(e, p)}
              onMouseLeave={() => setTip(null)}
              style={{ cursor: 'pointer' }}
            >
              <rect x={x} y={PAD.t} width={barW} height={innerH} fill="transparent" />
              <rect x={x} y={y} width={barW} height={barH} fill={color} opacity={0.8} rx={2} />
              {i % Math.max(1, Math.floor(data.length / 6)) === 0 && (
                <text x={x + barW / 2} y={H - PAD.b + 12} textAnchor="middle" fontSize={7} fill="#94a3b8">
                  {fmtDate(p.posted_at)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/* ── Donut distribución tipos ── */
function TypeDonut({ posts }) {
  const counts = {};
  posts.forEach(p => { counts[p.media_type] = (counts[p.media_type] || 0) + 1; });
  const total = posts.length;
  if (!total) return null;

  const types = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  const R = 45, CX = 60, CY = 60;
  let angle = -Math.PI / 2;

  const slices = types.map(([type, count]) => {
    const pct = count / total;
    const startAngle = angle;
    angle += pct * 2 * Math.PI;
    const endAngle = angle;
    const x1 = CX + R * Math.cos(startAngle);
    const y1 = CY + R * Math.sin(startAngle);
    const x2 = CX + R * Math.cos(endAngle);
    const y2 = CY + R * Math.sin(endAngle);
    const large = pct > 0.5 ? 1 : 0;
    return { type, count, pct, x1, y1, x2, y2, large };
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <svg viewBox="0 0 120 120" style={{ width: 100, height: 100, flexShrink: 0 }}>
        {slices.map(s => (
          <path key={s.type}
            d={`M${CX},${CY} L${s.x1},${s.y1} A${R},${R} 0 ${s.large},1 ${s.x2},${s.y2} Z`}
            fill={TYPE_META[s.type]?.color || '#94a3b8'}
            stroke="#fff" strokeWidth={2}
          />
        ))}
        <circle cx={CX} cy={CY} r={24} fill="#fff" />
        <text x={CX} y={CY + 4} textAnchor="middle" fontSize={11} fontWeight="700" fill="#1e293b">{total}</text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {slices.map(s => (
          <div key={s.type} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: TYPE_META[s.type]?.color || '#94a3b8', flexShrink: 0 }} />
            <span style={{ color: '#475569' }}>{TYPE_META[s.type]?.label || s.type}</span>
            <span style={{ color: '#94a3b8', marginLeft: 'auto', paddingLeft: 8 }}>
              {s.count} ({Math.round(s.pct * 100)}%)
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── ER line chart ── */
function ErChart({ posts }) {
  const [tip, setTip] = useState(null);
  const wrapRef = useRef(null);

  const data = posts
    .filter(p => p.engagement_rate != null)
    .slice().sort((a, b) => new Date(a.posted_at) - new Date(b.posted_at))
    .slice(-30);

  if (data.length < 2) return <div className="rg2-chart-empty">Pocos datos de ER</div>;

  const W = 600, H = 110, PAD = { t: 12, r: 8, b: 24, l: 40 };
  const innerW = W - PAD.l - PAD.r;
  const innerH = H - PAD.t - PAD.b;
  const vals = data.map(p => p.engagement_rate);
  const maxVal = Math.max(...vals, 0.01);
  const avg = vals.reduce((a, b) => a + b, 0) / vals.length;

  const pts = data.map((p, i) => {
    const x = PAD.l + (i / (data.length - 1)) * innerW;
    const y = PAD.t + innerH - (p.engagement_rate / maxVal) * innerH;
    return { x, y, p };
  });

  const polyline = pts.map(pt => `${pt.x},${pt.y}`).join(' ');
  const area = `M${pts[0].x},${PAD.t + innerH} ` +
    pts.map(pt => `L${pt.x},${pt.y}`).join(' ') +
    ` L${pts[pts.length-1].x},${PAD.t + innerH} Z`;
  const avgY = PAD.t + innerH - (avg / maxVal) * innerH;

  const showTip = (e, pt) => {
    const rect = wrapRef.current.getBoundingClientRect();
    setTip({ x: e.clientX - rect.left, y: e.clientY - rect.top, label: fmtDate(pt.p.posted_at), value: fmtPct(pt.p.engagement_rate) });
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <ChartTooltip tip={tip} />
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }} onMouseLeave={() => setTip(null)}>
        <defs>
          <linearGradient id="erGradVista" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#00c9a7" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#00c9a7" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0, 0.25, 0.5, 0.75, 1].map((f, i) => {
          const y = PAD.t + innerH * f;
          const val = maxVal * (1 - f);
          return (
            <g key={i}>
              <line x1={PAD.l} x2={W - PAD.r} y1={y} y2={y} stroke="#f1f5f9" strokeWidth={1} />
              <text x={PAD.l - 4} y={y + 4} textAnchor="end" fontSize={7} fill="#94a3b8">{val.toFixed(2)}%</text>
            </g>
          );
        })}
        <line x1={PAD.l} x2={W - PAD.r} y1={avgY} y2={avgY}
          stroke="#00694f" strokeWidth={0.8} strokeDasharray="4 3" opacity={0.4} />
        <path d={area} fill="url(#erGradVista)" />
        <polyline points={polyline} fill="none" stroke="#00694f" strokeWidth={1.5} strokeLinejoin="round" />
        {pts.map((pt, i) => (
          <g key={i} onMouseEnter={e => showTip(e, pt)} onMouseMove={e => showTip(e, pt)} onMouseLeave={() => setTip(null)} style={{ cursor: 'pointer' }}>
            <circle cx={pt.x} cy={pt.y} r={6} fill="transparent" />
            <circle cx={pt.x} cy={pt.y} r={2} fill="#00694f" />
            {i % Math.max(1, Math.floor(data.length / 6)) === 0 && (
              <text x={pt.x} y={H - PAD.b + 12} textAnchor="middle" fontSize={7} fill="#94a3b8">
                {fmtDate(pt.p.posted_at)}
              </text>
            )}
          </g>
        ))}
      </svg>
    </div>
  );
}

/* ── Top posts ── */
function TopPosts({ posts, metric, label, color, badgeLabel }) {
  const sorted = [...posts]
    .filter(p => p[metric] != null)
    .sort((a, b) => (b[metric] || 0) - (a[metric] || 0))
    .slice(0, 5);

  if (!sorted.length) return null;
  const max = sorted[0][metric] || 1;

  return (
    <div>
      {sorted.map((p, i) => (
        <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 11, color: '#94a3b8', width: 14 }}>{i + 1}</span>
          {p.media_type ? <TypeBadge mediaType={p.media_type} /> : (
            badgeLabel ? <span style={{ display:'inline-block',padding:'2px 8px',borderRadius:20,fontSize:10,fontWeight:700,background:'#fce7f3',color:'#be185d' }}>{badgeLabel}</span> : null
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ height: 6, background: '#f1f5f9', borderRadius: 3 }}>
              <div style={{ height: '100%', width: `${((p[metric] || 0) / max) * 100}%`, background: color, borderRadius: 3 }} />
            </div>
            <div style={{ fontSize: 10, color: '#64748b', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {fmtDate(p.posted_at)} · {(p.caption || '—').slice(0, 50)}
            </div>
          </div>
          <span style={{ fontSize: 12, fontWeight: 700, color, whiteSpace: 'nowrap' }}>
            {fmtN(p[metric])}
          </span>
          {p.permalink && (
            <a href={p.permalink} target="_blank" rel="noreferrer"
              style={{ fontSize: 10, color: '#6366f1', textDecoration: 'none' }}>↗</a>
          )}
        </div>
      ))}
    </div>
  );
}

/* ── Panel de análisis de un perfil (Instagram) ── */
function ProfileReport({ profile, since, until }) {
  const [posts, setPosts]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [loaded, setLoaded]   = useState(false);
  const [chartTab, setChartTab] = useState('likes');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = { since, until, limit: 500, ordering: '-posted_at' };
      if (!profile.is_marca) params.profile_id = profile.id;
      const res = await contentService.getPublicMarcaPosts(params);
      setPosts(res.data.posts || []);
      setLoaded(true);
    } catch (e) {
      setError('Error al cargar los posts.');
    } finally {
      setLoading(false);
    }
  }, [profile.id, profile.is_marca, since, until]);

  useEffect(() => { load(); }, [load]);

  const kpis = useMemo(() => {
    if (!posts.length) return null;
    const totLikes    = posts.reduce((s, p) => s + (p.like_count || 0), 0);
    const totComments = posts.reduce((s, p) => s + (p.comments_count || 0), 0);
    const totViews    = posts.reduce((s, p) => s + (p.views_count || 0), 0);
    const erList = posts.filter(p => p.engagement_rate != null).map(p => p.engagement_rate);
    const vrList = posts.filter(p => p.view_rate != null).map(p => p.view_rate);
    const reels  = posts.filter(p => p.media_type === 'VIDEO');
    return {
      total: posts.length,
      totLikes, totComments, totViews,
      avgEr: erList.length ? erList.reduce((a, b) => a + b, 0) / erList.length : null,
      avgVr: vrList.length ? vrList.reduce((a, b) => a + b, 0) / vrList.length : null,
      reels: reels.length,
      fotos: posts.filter(p => p.media_type === 'IMAGE').length,
      carruseles: posts.filter(p => p.media_type === 'CAROUSEL_ALBUM').length,
    };
  }, [posts]);

  const CHART_OPTS = [
    { key: 'like_count',     label: 'Likes',        color: '#e1306c' },
    { key: 'comments_count', label: 'Comentarios',  color: '#6366f1' },
    { key: 'views_count',    label: 'Vistas',       color: '#0891b2' },
  ];
  const activeChart = CHART_OPTS.find(o => o.key === chartTab) || CHART_OPTS[0];

  if (loading) return <Loading fullScreen={false} mensaje={`Cargando posts de @${profile.username}`} />;
  if (error)   return <div className="rg2-error">{error}</div>;
  if (loaded && !posts.length) return (
    <div className="rg2-empty">No hay posts de @{profile.username} en este periodo.</div>
  );
  if (!loaded) return null;

  return (
    <div className="rg2-report">
      <div className="rg2-kpis">
        <KpiCard label="Posts"       value={kpis.total} />
        <KpiCard label="Likes"       value={fmtN(kpis.totLikes)}    color="#e1306c" />
        <KpiCard label="Comentarios" value={fmtN(kpis.totComments)} color="#6366f1" />
        <KpiCard label="Vistas"      value={fmtN(kpis.totViews)}    color="#0891b2" />
        <KpiCard label="ER prom."    value={fmtPct(kpis.avgEr)}     color="#10b981"
          sub={kpis.avgVr != null ? `VR reels: ${fmtPct(kpis.avgVr)}` : null} />
        <KpiCard label="Reels"       value={kpis.reels}  color="#e1306c" />
        <KpiCard label="Fotos"       value={kpis.fotos}  color="#1565c0" />
        <KpiCard label="Carruseles"  value={kpis.carruseles} color="#4527a0" />
      </div>

      <div className="rg2-charts-grid">
        <div className="rg2-chart-card rg2-chart-wide">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Evolución por post</span>
            <div className="rg2-chart-tabs">
              {CHART_OPTS.map(o => (
                <button key={o.key}
                  className={`rg2-chart-tab${chartTab === o.key ? ' active' : ''}`}
                  style={{ '--tab-color': o.color }}
                  onClick={() => setChartTab(o.key)}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
          <BarChart posts={posts} metric={activeChart.key} color={activeChart.color} label={activeChart.label} />
        </div>

        <div className="rg2-chart-card rg2-chart-wide">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Engagement Rate (ER) por post</span>
          </div>
          <ErChart posts={posts} />
        </div>

        <div className="rg2-chart-card">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Distribución de contenido</span>
          </div>
          <TypeDonut posts={posts} />
        </div>

        <div className="rg2-chart-card">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Top posts por likes</span>
          </div>
          <TopPosts posts={posts} metric="like_count" label="Likes" color="#e1306c" />
        </div>

        <div className="rg2-chart-card">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Top reels por vistas</span>
          </div>
          <TopPosts posts={posts.filter(p => p.media_type === 'VIDEO')} metric="views_count" label="Vistas" color="#0891b2" />
        </div>

        <div className="rg2-chart-card">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Top posts por comentarios</span>
          </div>
          <TopPosts posts={posts} metric="comments_count" label="Coment." color="#6366f1" />
        </div>

        <div className="rg2-chart-card">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Top posts por ER</span>
          </div>
          <TopPosts posts={posts} metric="engagement_rate" label="ER" color="#10b981" />
        </div>
      </div>

      <div className="rg2-table-wrap">
        <table className="rg2-table">
          <thead>
            <tr>
              <th>Post</th>
              <th>Fecha</th>
              <th className="rg2-num">Likes</th>
              <th className="rg2-num">Coment.</th>
              <th className="rg2-num">Vistas</th>
              <th className="rg2-num">ER</th>
              <th className="rg2-num">VR</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {posts.map(p => (
              <tr key={p.id} className="rg2-row">
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {p.thumbnail_url ? (
                      <img src={p.thumbnail_url} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6 }}
                        onError={e => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div style={{ width: 36, height: 36, background: '#f1f5f9', borderRadius: 6 }} />
                    )}
                    <div style={{ minWidth: 0 }}>
                      <TypeBadge mediaType={p.media_type} />
                      <div style={{ fontSize: 11, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>
                        {p.caption || <span style={{ color: '#cbd5e1', fontStyle: 'italic' }}>Sin caption</span>}
                      </div>
                    </div>
                  </div>
                </td>
                <td style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>{fmtDate(p.posted_at)}</td>
                <td className="rg2-num">{fmtN(p.like_count)}</td>
                <td className="rg2-num">{fmtN(p.comments_count)}</td>
                <td className="rg2-num">{fmtN(p.views_count)}</td>
                <td className="rg2-num" style={{ color: '#00694f', fontWeight: 700 }}>{fmtPct(p.engagement_rate)}</td>
                <td className="rg2-num" style={{ color: '#00c9a7', fontWeight: 700 }}>
                  {p.view_rate != null ? fmtPct(p.view_rate) : <span style={{ color: '#cdd5d3' }}>—</span>}
                </td>
                <td>
                  {p.permalink && (
                    <a href={p.permalink} target="_blank" rel="noreferrer"
                      style={{ fontSize: 11, color: '#6366f1', textDecoration: 'none', fontWeight: 600 }}>
                      Ver ↗
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Gráfico de evolución de seguidores ── */
function FollowersChart({ since, until, profileId }) {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [tip,     setTip]     = useState(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    const params = { since, until };
    if (profileId) params.profile_id = profileId;
    contentService.getPublicMercadoFollowersHistory(params)
      .then(r => setData(r.data.profiles || []))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [since, until, profileId]);

  if (loading) return <Loading fullScreen={false} mensaje="Cargando" />;

  const profile = data[0];
  const igSnaps = profile?.snapshots_instagram || profile?.snapshots || [];
  const tkSnaps = profile?.snapshots_tiktok    || [];

  const allDatesSet = new Set([...igSnaps.map(s => s.date), ...tkSnaps.map(s => s.date)]);
  const allDates = [...allDatesSet].sort();

  const igByDate = Object.fromEntries(igSnaps.map(s => [s.date, s.followers_count]));
  const tkByDate = Object.fromEntries(tkSnaps.map(s => [s.date, s.followers_count]));

  const totalSnaps = allDates
    .filter(d => igByDate[d] != null || tkByDate[d] != null)
    .map(d => ({ date: d, followers_count: (igByDate[d] || 0) + (tkByDate[d] || 0) }));

  const SERIES = [
    { key: 'ig',    label: 'Instagram', color: '#e1306c', snaps: igSnaps,    dashed: false },
    { key: 'tk',    label: 'TikTok',    color: '#010101', snaps: tkSnaps,    dashed: false },
    { key: 'total', label: 'Total',     color: '#00c9a7', snaps: totalSnaps, dashed: true  },
  ].filter(s => s.snaps.length >= 1);

  if (!SERIES.length || allDates.length < 2) return (
    <div className="rg2-chart-card rg2-chart-wide" style={{ marginBottom: 16 }}>
      <div className="rg2-chart-header">
        <span className="rg2-chart-title">Evolución de seguidores</span>
      </div>
      <div className="rg2-chart-empty">Aún no hay datos históricos — se irán acumulando con cada ronda del scraper.</div>
    </div>
  );

  const W = 700, H = 200, PAD = { t: 16, r: 60, b: 28, l: 60 };
  const innerW = W - PAD.l - PAD.r;
  const innerH = H - PAD.t - PAD.b;

  const allVals = SERIES.flatMap(s => s.snaps.map(sn => sn.followers_count));
  const dataMin = Math.min(...allVals);
  const dataMax = Math.max(...allVals);
  const pad = Math.max((dataMax - dataMin) * 0.5, dataMax * 0.002, 100);
  const minVal = dataMin - pad;
  const maxVal = dataMax + pad;
  const range  = maxVal - minVal;

  const baseVal = totalSnaps[0]?.followers_count || igSnaps[0]?.followers_count || dataMin;

  const toX = (date) => {
    const idx = allDates.indexOf(date);
    return PAD.l + (idx / Math.max(allDates.length - 1, 1)) * innerW;
  };
  const toY = (val) => PAD.t + innerH - ((val - minVal) / range) * innerH;

  const yTicks = 4;

  const showTip = (e, label, value) => {
    const rect = wrapRef.current.getBoundingClientRect();
    setTip({ x: e.clientX - rect.left, y: e.clientY - rect.top, label, value });
  };

  return (
    <div className="rg2-chart-card rg2-chart-wide" style={{ marginBottom: 16 }}>
      <div className="rg2-chart-header">
        <span className="rg2-chart-title">Evolución de seguidores</span>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {SERIES.map(s => (
            <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11 }}>
              <div style={{ width: 14, height: 2, borderRadius: 1, background: s.color,
                borderBottom: s.dashed ? `2px dashed ${s.color}` : 'none',
                height: s.dashed ? 0 : 2 }} />
              <span style={{ color: '#4a5854', fontWeight: 600 }}>{s.label}</span>
            </div>
          ))}
        </div>
      </div>
      <div ref={wrapRef} style={{ position: 'relative' }}>
        <ChartTooltip tip={tip} />
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }} onMouseLeave={() => setTip(null)}>
          {Array.from({ length: yTicks + 1 }, (_, i) => {
            const frac = i / yTicks;
            const y = PAD.t + innerH * frac;
            const val = maxVal - range * frac;
            const pct = ((val - baseVal) / baseVal * 100);
            return (
              <g key={i}>
                <line x1={PAD.l} x2={W - PAD.r} y1={y} y2={y} stroke="#f1f5f9" strokeWidth={1} />
                <text x={PAD.l - 4} y={y + 4} textAnchor="end" fontSize={7} fill="#94a3b8">
                  {fmtAxis(Math.round(val), dataMax - dataMin)}
                </text>
                <text x={W - PAD.r + 4} y={y + 4} textAnchor="start" fontSize={7}
                  fill={pct > 0 ? '#00694f' : pct < 0 ? '#ef4444' : '#94a3b8'}>
                  {pct > 0 ? '+' : ''}{pct.toFixed(2)}%
                </text>
              </g>
            );
          })}

          {SERIES.map(s => {
            const pts = s.snaps.map(sn => ({ x: toX(sn.date), y: toY(sn.followers_count), sn }));
            const polyline = pts.map(pt => `${pt.x},${pt.y}`).join(' ');
            return (
              <g key={s.key}>
                <polyline
                  points={polyline}
                  fill="none"
                  stroke={s.color}
                  strokeWidth={s.key === 'total' ? 1 : 1.5}
                  strokeDasharray={s.dashed ? '4 3' : undefined}
                  strokeLinejoin="round"
                  opacity={s.key === 'total' ? 0.85 : 1}
                />
                {pts.map((pt, i) => (
                  <g key={i}
                    onMouseEnter={e => showTip(e, `${s.label} · ${fmtDate(pt.sn.date)}`, `${Number(pt.sn.followers_count).toLocaleString('es-CL')} seg.`)}
                    onMouseMove={e  => showTip(e, `${s.label} · ${fmtDate(pt.sn.date)}`, `${Number(pt.sn.followers_count).toLocaleString('es-CL')} seg.`)}
                    onMouseLeave={() => setTip(null)}
                    style={{ cursor: 'pointer' }}
                  >
                    <circle cx={pt.x} cy={pt.y} r={6} fill="transparent" />
                    <circle cx={pt.x} cy={pt.y} r={s.key === 'total' ? 1.5 : 2} fill={s.color} opacity={s.key === 'total' ? 0.7 : 1} />
                  </g>
                ))}
              </g>
            );
          })}

          {allDates.map((d, i) => (
            (i === 0 || i === allDates.length - 1 || i % Math.max(1, Math.floor(allDates.length / 5)) === 0) && (
              <text key={d} x={toX(d)} y={H - PAD.b + 12} textAnchor="middle" fontSize={7} fill="#94a3b8">
                {fmtDate(d)}
              </text>
            )
          ))}
        </svg>
      </div>
    </div>
  );
}

/* ── Panel TikTok de un perfil ── */
function TikTokReport({ profile, since, until }) {
  const [videos, setVideos]     = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [loaded, setLoaded]     = useState(false);
  const [chartTab, setChartTab] = useState('like_count');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = { profile_id: profile.id, since, until, limit: 500, ordering: '-posted_at' };
      const res = await contentService.getPublicMercadoTikTokPosts(params);
      setVideos(res.data.posts || []);
      setLoaded(true);
    } catch (e) {
      setError('Error al cargar los videos de TikTok.');
    } finally {
      setLoading(false);
    }
  }, [profile.id, since, until]);

  useEffect(() => { load(); }, [load]);

  const kpis = useMemo(() => {
    if (!videos.length) return null;
    const totLikes    = videos.reduce((s, v) => s + (v.like_count || 0), 0);
    const totComments = videos.reduce((s, v) => s + (v.comments_count || 0), 0);
    const totShares   = videos.reduce((s, v) => s + (v.share_count || 0), 0);
    const totViews    = videos.reduce((s, v) => s + (v.views_count || 0), 0);
    const totSaves    = videos.reduce((s, v) => s + (v.saves_count || 0), 0);
    const erList = videos.filter(v => v.engagement_rate != null).map(v => v.engagement_rate);
    const vrList = videos.filter(v => v.view_rate != null).map(v => v.view_rate);
    return {
      total: videos.length,
      totLikes, totComments, totShares, totViews, totSaves,
      avgEr: erList.length ? erList.reduce((a, b) => a + b, 0) / erList.length : null,
      avgVr: vrList.length ? vrList.reduce((a, b) => a + b, 0) / vrList.length : null,
    };
  }, [videos]);

  const CHART_OPTS = [
    { key: 'like_count',      label: 'Likes',       color: '#e1306c' },
    { key: 'views_count',     label: 'Vistas',      color: '#0891b2' },
    { key: 'comments_count',  label: 'Comentarios', color: '#6366f1' },
    { key: 'share_count',     label: 'Shares',      color: '#f59e0b' },
    { key: 'saves_count',     label: 'Guardados',   color: '#10b981' },
  ];
  const activeChart = CHART_OPTS.find(o => o.key === chartTab) || CHART_OPTS[0];

  if (loading) return <Loading fullScreen={false} mensaje={`Cargando TikTok de @${profile.tiktok_username}`} />;
  if (error)   return <div className="rg2-error">{error}</div>;
  if (loaded && !videos.length) return (
    <div className="rg2-empty">No hay videos de @{profile.tiktok_username} en este periodo.</div>
  );
  if (!loaded) return null;

  return (
    <div className="rg2-report">
      <div className="rg2-kpis">
        <KpiCard label="Videos"      value={kpis.total} />
        <KpiCard label="Likes"       value={fmtN(kpis.totLikes)}    color="#e1306c" />
        <KpiCard label="Comentarios" value={fmtN(kpis.totComments)} color="#6366f1" />
        <KpiCard label="Shares"      value={fmtN(kpis.totShares)}   color="#f59e0b" />
        <KpiCard label="Vistas"      value={fmtN(kpis.totViews)}    color="#0891b2" />
        <KpiCard label="Guardados"   value={fmtN(kpis.totSaves)}    color="#10b981" />
        <KpiCard label="ER prom."    value={fmtPct(kpis.avgEr)}     color="#8b5cf6"
          sub={kpis.avgVr != null ? `VR: ${fmtPct(kpis.avgVr)}` : null} />
      </div>

      <div className="rg2-charts-grid">
        <div className="rg2-chart-card rg2-chart-wide">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Evolución por video</span>
            <div className="rg2-chart-tabs">
              {CHART_OPTS.map(o => (
                <button key={o.key}
                  className={`rg2-chart-tab${chartTab === o.key ? ' active' : ''}`}
                  style={{ '--tab-color': o.color }}
                  onClick={() => setChartTab(o.key)}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
          <BarChart posts={videos} metric={activeChart.key} color={activeChart.color} label={activeChart.label} />
        </div>

        <div className="rg2-chart-card rg2-chart-wide">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Engagement Rate (ER) por video</span>
          </div>
          <ErChart posts={videos} />
        </div>

        <div className="rg2-chart-card">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Top videos por vistas</span>
          </div>
          <TopPosts posts={videos} metric="views_count" label="Vistas" color="#0891b2" badgeLabel="TikTok" />
        </div>

        <div className="rg2-chart-card">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Top videos por likes</span>
          </div>
          <TopPosts posts={videos} metric="like_count" label="Likes" color="#e1306c" badgeLabel="TikTok" />
        </div>

        <div className="rg2-chart-card">
          <div className="rg2-chart-header">
            <span className="rg2-chart-title">Top videos por ER</span>
          </div>
          <TopPosts posts={videos} metric="engagement_rate" label="ER" color="#8b5cf6" badgeLabel="TikTok" />
        </div>
      </div>

      <div className="rg2-table-wrap">
        <table className="rg2-table">
          <thead>
            <tr>
              <th>Video</th>
              <th>Fecha</th>
              <th className="rg2-num">Likes</th>
              <th className="rg2-num">Coment.</th>
              <th className="rg2-num">Shares</th>
              <th className="rg2-num">Vistas</th>
              <th className="rg2-num">Guardados</th>
              <th className="rg2-num">ER</th>
              <th className="rg2-num">VR</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {videos.map(v => (
              <tr key={v.id} className="rg2-row">
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {v.cover_url ? (
                      <img src={v.cover_url} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6 }}
                        onError={e => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div style={{ width: 36, height: 36, background: '#f1f5f9', borderRadius: 6 }} />
                    )}
                    <div style={{ minWidth: 0 }}>
                      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: '#fce7f3', color: '#be185d' }}>
                        {v.is_pinned ? '📌 TikTok' : 'TikTok'}
                      </span>
                      <div style={{ fontSize: 11, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>
                        {v.caption || <span style={{ color: '#cbd5e1', fontStyle: 'italic' }}>Sin caption</span>}
                      </div>
                    </div>
                  </div>
                </td>
                <td style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>{fmtDate(v.posted_at)}</td>
                <td className="rg2-num">{fmtN(v.like_count)}</td>
                <td className="rg2-num">{fmtN(v.comments_count)}</td>
                <td className="rg2-num">{fmtN(v.share_count)}</td>
                <td className="rg2-num">{fmtN(v.views_count)}</td>
                <td className="rg2-num">{fmtN(v.saves_count)}</td>
                <td className="rg2-num" style={{ color: '#00694f', fontWeight: 700 }}>{fmtPct(v.engagement_rate)}</td>
                <td className="rg2-num" style={{ color: '#00c9a7', fontWeight: 700 }}>
                  {v.view_rate != null ? fmtPct(v.view_rate) : <span style={{ color: '#cdd5d3' }}>—</span>}
                </td>
                <td>
                  {v.permalink && (
                    <a href={v.permalink} target="_blank" rel="noreferrer"
                      style={{ fontSize: 11, color: '#6366f1', textDecoration: 'none', fontWeight: 600 }}>
                      Ver ↗
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Componente principal — vista pública ── */
export default function ReporteGevesVista() {
  const [since, setSince] = useState(iso30DaysAgo);
  const [until, setUntil] = useState(isoToday);
  const [appliedSince, setAppliedSince] = useState(iso30DaysAgo);
  const [appliedUntil, setAppliedUntil] = useState(isoToday);

  const [mercado, setMercado]   = useState(null);
  const [loadingMercado, setLoadingMercado] = useState(true);
  const [activeTab, setActiveTab] = useState(null);
  const [platform, setPlatform] = useState('instagram');

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setPlatform('instagram');
  };

  useEffect(() => {
    contentService.getPublicMercado()
      .then(r => {
        setMercado(r.data);
        setActiveTab(null);
      })
      .catch(() => setMercado(null))
      .finally(() => setLoadingMercado(false));
  }, []);

  const handleBuscar = () => {
    setAppliedSince(since);
    setAppliedUntil(until);
  };

  // Solo Marca + Competencia (sin Referencia)
  const tabs = useMemo(() => {
    if (!mercado) return [];
    const list = [];
    if (mercado.marca) {
      list.push({ ...mercado.marca, role: 'Mi Marca', is_marca: true, tabId: null });
    }
    (mercado.competencia || []).forEach(p => list.push({ ...p, role: 'Competencia', tabId: p.id }));
    return list;
  }, [mercado]);

  const activeProfile = useMemo(() => {
    if (!tabs.length) return null;
    return tabs.find(t => t.tabId === activeTab) || tabs[0];
  }, [tabs, activeTab]);

  if (loadingMercado) {
    return (
      <div className="rgv-public-page">
        <div className="rgv-public-header">
          <span className="rgv-logo">Geves</span>
        </div>
        <div className="rg2-page"><Loading fullScreen={false} mensaje="Cargando reporte" /></div>
      </div>
    );
  }

  if (!mercado?.marca && !tabs.length) {
    return (
      <div className="rgv-public-page">
        <div className="rgv-public-header">
          <span className="rgv-logo">Geves</span>
        </div>
        <div className="rg2-page">
          <div className="rg2-empty-state">
            <div style={{ fontSize: 40 }}>📊</div>
            <p style={{ fontWeight: 600, color: '#475569', marginTop: 12 }}>Reporte no disponible</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rgv-public-page">
      {/* Header público — sin navegación */}
      <div className="rgv-public-header">
        <span className="rgv-logo">Geves</span>
        <span className="rgv-header-title">Reporte de Mercado</span>
      </div>

      <div className="rg2-page" style={{ paddingTop: 0 }}>
        {/* Filtro de fechas */}
        <div className="rg2-header">
          <div className="rg2-date-row">
            <label className="rg2-date-label">Desde</label>
            <input type="date" className="rg2-date-input" value={since}
              onChange={e => setSince(e.target.value)} max={until} />
            <label className="rg2-date-label">Hasta</label>
            <input type="date" className="rg2-date-input" value={until}
              onChange={e => setUntil(e.target.value)} min={since} max={isoToday()} />
            <button className="rg2-btn-buscar" onClick={handleBuscar}>Buscar</button>
          </div>
        </div>

        {/* Tabs de perfiles */}
        <div className="rg2-profile-tabs">
          {tabs.map(t => (
            <button
              key={t.tabId ?? 'marca'}
              className={`rg2-profile-tab${activeTab === t.tabId ? ' active' : ''}`}
              onClick={() => handleTabChange(t.tabId)}
            >
              <Avatar profile={t} size={28} />
              <div style={{ minWidth: 0, textAlign: 'left' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  @{t.username}
                </div>
                <div style={{ fontSize: 10, opacity: 0.7 }}>{t.role}</div>
              </div>
            </button>
          ))}
        </div>

        {/* Platform switcher */}
        {activeProfile?.tiktok_username && (
          <div className="rg2-platform-switcher" style={{ display: 'flex', gap: 8, padding: '0 0 16px 0', marginBottom: 4 }}>
            <button
              onClick={() => setPlatform('instagram')}
              style={{
                padding: '6px 18px', borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                fontFamily: 'inherit', letterSpacing: '0.01em',
                border: platform === 'instagram' ? '2px solid #002421' : '1.5px solid rgba(0,36,33,0.15)',
                background: platform === 'instagram' ? '#002421' : '#fff',
                color: platform === 'instagram' ? '#fff' : '#4a5854',
              }}
            >
              Instagram
            </button>
            <button
              onClick={() => setPlatform('tiktok')}
              style={{
                padding: '6px 18px', borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                fontFamily: 'inherit', letterSpacing: '0.01em',
                border: platform === 'tiktok' ? '2px solid #002421' : '1.5px solid rgba(0,36,33,0.15)',
                background: platform === 'tiktok' ? '#002421' : '#fff',
                color: platform === 'tiktok' ? '#fff' : '#4a5854',
              }}
            >
              TikTok @{activeProfile.tiktok_username}
            </button>
          </div>
        )}

        {/* Reporte del perfil activo */}
        {activeProfile && platform === 'instagram' && (
          <ProfileReport
            key={`ig-${activeProfile.id}-${appliedSince}-${appliedUntil}`}
            profile={activeProfile}
            since={appliedSince}
            until={appliedUntil}
          />
        )}
        {activeProfile?.tiktok_username && platform === 'tiktok' && (
          <TikTokReport
            key={`tt-${activeProfile.id}-${appliedSince}-${appliedUntil}`}
            profile={activeProfile}
            since={appliedSince}
            until={appliedUntil}
          />
        )}
      </div>
    </div>
  );
}
